import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext, type RaRecord } from 'react-admin';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { recordScoped, useStillMounted } from '../../shared/recordScoped';
import { TABLE_SCROLL_SX } from '../../shared/tableScroll';
import { toErrorMessage } from './orderActionsApi';
import { searchCarriers } from './carrierInquiriesApi';
import { useFeature } from '../featureFlags/FeatureGate';
import { useElementVisible, useHasPermission } from '../PermissionGate';
import { ELEMENT } from '../roleCenter/elementKeys';
import { ModuleGate, companyIdOf } from '../ModuleGate';
import {
  FEATURE_CARRIER_ACCEPTANCE,
  FEATURE_CARRIER_OFFERS,
  MIN_ACCEPTANCE_REASON,
  acceptCarrierOffer,
  revokeCarrierAcceptance,
  MARGIN_BAND_LABELS,
  marginBandColor,
  OFFER_CURRENCIES,
  OFFER_KIND_CHOICES,
  OFFER_SOURCE_CHOICES,
  OFFER_STATUS_LABELS,
  createCarrierOffer,
  fetchCarrierOffers,
  marginHint,
  marginText,
  money,
  offerKindLabel,
  offerSourceLabel,
  rejectCarrierOffer,
  type CarrierOffer,
  type CarrierOffersResponse,
  type NewCarrierOffer,
} from './carrierOffersApi';

/**
 * „Oferty przewoźników” na karcie zlecenia w Trade (makieta back office v2,
 * A9: BR-TRD-08 część, TRD-06 część; decyzja D10 z 20.10.2026; przełącznik
 * `carrierOfferRegister`).
 *
 * Trader zapisuje ofertę przewoźnika albo swoją kontrofertę — z telefonu,
 * maila albo giełdy. Przy każdej marża wobec ceny zlecenia (kurs NBP z dnia
 * załadunku, ta sama reguła co w rekomendacjach); pasmo marży tylko przy
 * wpisanych progach — dziś bez alertu, a pasmo i tak niczego nie blokuje.
 * **Nic nie wychodzi na zewnątrz** i nic nie zmienia stanu zlecenia;
 * zatwierdzenie przewoźnika to kolejny etap (fala B).
 *
 * ## Akceptacja przewoźnika (paczka p. Mariusza 21.10, S-21.10-17)
 *
 * Z przełącznikiem `carrierAcceptance` przy otwartej ofercie przycisk
 * „Akceptuj przewoźnika i stawkę” (BR-TRD-02): okno z przewoźnikiem, stawką
 * i marżą, warunki i uzasadnienie — wymagane przy ostrzeżeniu z API. Po
 * akceptacji baner: kto i kiedy, że oferty schodzą z giełd, a „sprzedane”
 * ustawi import z TMS; akceptację można cofnąć z powodem. Zwrot do FTL
 * zostaje możliwy (decyzja z 21.10.2026).
 */

export const OFFERS_NOTE =
  'Wpisz ofertę przewoźnika albo swoją kontrofertę — z telefonu, maila albo giełdy. Nic stąd nie wychodzi do przewoźnika ani na giełdę i wpis nie zmienia stanu zlecenia. Marża liczona wobec ceny zlecenia kursem NBP z dnia załadunku.';

export const NO_THRESHOLDS_NOTE =
  'Progi alertu marży nie są ustawione — marża bez oznaczenia pasma.';

const EMPTY_FORM: Omit<NewCarrierOffer, 'carrierId'> = {
  kind: 'carrier_offer',
  amount: '',
  currency: 'EUR',
  validUntil: '',
  source: 'phone',
  sourceNote: '',
  note: '',
};

/** Czy formularz da się wysłać — kwota i przewoźnik, a przy „inne” opis źródła. */
export function offerFormReady(
  carrier: { id: number } | null,
  form: Omit<NewCarrierOffer, 'carrierId'>,
): boolean {
  if (!carrier) return false;
  if (!/^\d+([.,]\d{1,2})?$/.test(form.amount.trim().replace(/\s/g, ''))) return false;
  if (Number(form.amount.replace(',', '.')) <= 0) return false;
  if (form.source === 'other' && form.sourceNote.trim() === '') return false;
  return true;
}

const MarginCell = ({ offer }: { offer: CarrierOffer }) => {
  const m = offer.margin;
  return (
    <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
      <Tooltip title={marginHint(m)}>
        <Typography variant="body2" component="span" data-testid={`offer-margin-${offer.id}`}>
          {marginText(m)}
        </Typography>
      </Tooltip>
      {m.band ? (
        <Tooltip title={m.bandRule ?? ''}>
          <Chip
            size="small"
            variant="outlined"
            color={marginBandColor(m.band)}
            label={MARGIN_BAND_LABELS[m.band] ?? m.band}
            data-testid={`offer-margin-band-${offer.id}`}
          />
        </Tooltip>
      ) : null}
    </Stack>
  );
};

const OffersTable = ({
  offers,
  onReject,
  onAccept,
}: {
  offers: CarrierOffer[];
  onReject: (offer: CarrierOffer) => void;
  /** Bez przełącznika akceptacji — bez przycisku. */
  onAccept?: (offer: CarrierOffer) => void;
}) => (
  <Box sx={TABLE_SCROLL_SX}>
    <Table size="small" aria-label="Oferty przewoźników">
      <TableHead>
        <TableRow>
          <TableCell>Przewoźnik</TableCell>
          <TableCell>Kwota</TableCell>
          <TableCell>Marża</TableCell>
          <TableCell>Ważna do</TableCell>
          <TableCell>Źródło</TableCell>
          <TableCell>Stan</TableCell>
          <TableCell>Wpisał</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {offers.map((o) => (
          <TableRow key={o.id} data-testid={`offer-${o.id}`}>
            <TableCell>
              {o.carrier.name}
              <Typography variant="caption" color="text.secondary" component="p">
                {offerKindLabel(o.kind)}
                {o.note ? ` · ${o.note}` : ''}
              </Typography>
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{money(o.amount, o.currency)}</TableCell>
            <TableCell>
              <MarginCell offer={o} />
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {o.validUntil ? dateTimeNoSeconds(o.validUntil) : '—'}
              {o.expired && o.status === 'open' ? (
                <Chip size="small" variant="outlined" color="warning" label="po terminie" sx={{ ml: 0.5 }} />
              ) : null}
            </TableCell>
            <TableCell>
              {offerSourceLabel(o.source)}
              {o.sourceNote ? (
                <Typography variant="caption" color="text.secondary" component="p">
                  {o.sourceNote}
                </Typography>
              ) : null}
            </TableCell>
            <TableCell>
              {OFFER_STATUS_LABELS[o.status] ?? o.status}
              {o.status === 'accepted' && o.acceptance?.stale ? ' (nieaktualna — zlecenie wracało z Trade)' : ''}
              {o.status === 'accepted' && o.statusChangedBy ? (
                <Typography variant="caption" color="text.secondary" component="p">
                  {[o.statusChangedBy, o.statusChangedAt ? dateTimeNoSeconds(o.statusChangedAt) : null]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              ) : null}
              {o.status === 'rejected' && (o.statusReason || o.statusChangedBy) ? (
                <Typography variant="caption" color="text.secondary" component="p">
                  {[o.statusReason, o.statusChangedBy].filter(Boolean).join(' · ')}
                </Typography>
              ) : null}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {o.createdBy ?? '—'}
              <Typography variant="caption" color="text.secondary" component="p">
                {dateTimeNoSeconds(o.createdAt)}
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              {o.status === 'open' && onAccept && o.acceptance ? (
                <Tooltip title={o.acceptance.acceptRefusal ?? ''}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!o.acceptance.canAccept}
                      onClick={() => onAccept(o)}
                      data-testid="trd-negocjacja-akceptuj-przewoznika"
                    >
                      Akceptuj przewoźnika i stawkę
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
              {o.status === 'open' ? (
                <Button size="small" onClick={() => onReject(o)}>
                  Odrzuć
                </Button>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const CarrierOffersPanel = () => {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const [data, setData] = useState<CarrierOffersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [carrier, setCarrier] = useState<{ id: number; name: string } | null>(null);
  const [options, setOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState<CarrierOffer | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  // Akceptacja przewoźnika (S-21.10-17) — okno akceptacji i cofnięcia.
  const acceptanceOn = useFeature(FEATURE_CARRIER_ACCEPTANCE);
  const [accepting, setAccepting] = useState<CarrierOffer | null>(null);
  const [conditions, setConditions] = useState('');
  const [justification, setJustification] = useState('');
  const [revoking, setRevoking] = useState<CarrierOffer | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const id = record?.id;

  const load = useCallback(async () => {
    if (id === undefined) return;
    try {
      const result = await fetchCarrierOffers(id);
      if (stillMounted()) {
        setData(result);
        setError(null);
      }
    } catch (e) {
      if (stillMounted()) setError(toErrorMessage(e));
    }
  }, [id, stillMounted]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!record) return null;

  const search = async (q: string) => {
    if (q.trim().length < 2) return;
    try {
      setOptions(await searchCarriers(q.trim()));
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    }
  };

  const submit = async () => {
    if (!carrier) return;
    setBusy(true);
    try {
      await createCarrierOffer(record.id, { ...form, carrierId: carrier.id });
      notify('Oferta zapisana — nic nie zostało wysłane do przewoźnika', { type: 'success' });
      setForm(EMPTY_FORM);
      setCarrier(null);
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejecting) return;
    setBusy(true);
    try {
      await rejectCarrierOffer(record.id, rejecting.id, rejectReason);
      notify('Oferta odrzucona', { type: 'success' });
      setRejecting(null);
      setRejectReason('');
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const closeAccept = () => {
    setAccepting(null);
    setConditions('');
    setJustification('');
  };
  const acceptWarnings = accepting?.acceptance?.warnings ?? [];
  const acceptReady =
    acceptWarnings.length === 0 || justification.trim().length >= MIN_ACCEPTANCE_REASON;

  const accept = async () => {
    if (!accepting) return;
    setBusy(true);
    try {
      await acceptCarrierOffer(record.id, accepting.id, { conditions, justification });
      notify('Przewoźnik zaakceptowany — oferty na giełdach zejdą w ciągu minuty', { type: 'success' });
      closeAccept();
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!revoking) return;
    setBusy(true);
    try {
      await revokeCarrierAcceptance(record.id, revoking.id, revokeReason);
      notify('Akceptacja cofnięta — oferta wróciła do otwartych', { type: 'success' });
      setRevoking(null);
      setRevokeReason('');
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const accepted =
    acceptanceOn && data?.acceptedOfferId != null
      ? (data.offers.find((o) => o.id === data.acceptedOfferId) ?? null)
      : null;

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const value = data?.orderValue;

  return (
    <SectionCard title={`Oferty przewoźników${data ? ` (${data.offers.length})` : ''}`}>
      <Stack spacing={1.5} data-testid="carrier-offers">
        <Typography variant="body2" color="text.secondary">
          {OFFERS_NOTE}
        </Typography>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {value ? (
          <Typography variant="body2" data-testid="carrier-offers-order-value">
            Cena zlecenia: {money(value.amount, value.currency)}
            {value.pln !== null && value.currency !== 'PLN' ? ` (≈ ${money(value.pln, 'PLN')})` : ''}
            {value.missing.length > 0 ? ` — nie policzono: ${value.missing.join('; ')}` : ''}
          </Typography>
        ) : null}
        {data && !data.marginAlertConfigured ? (
          <Typography variant="caption" color="text.secondary">
            {NO_THRESHOLDS_NOTE}
          </Typography>
        ) : null}
        {accepted ? (
          <Alert
            severity="success"
            data-testid="carrier-accepted"
            action={
              <Button color="inherit" size="small" onClick={() => setRevoking(accepted)}>
                Cofnij akceptację
              </Button>
            }
          >
            Przewoźnik zaakceptowany: <strong>{accepted.carrier.name}</strong>, stawka{' '}
            {money(accepted.amount, accepted.currency)}
            {accepted.acceptance?.conditions ? `, warunki: ${accepted.acceptance.conditions}` : ''} — zatwierdził{' '}
            {accepted.statusChangedBy ?? '—'}
            {accepted.statusChangedAt ? `, ${dateTimeNoSeconds(accepted.statusChangedAt)}` : ''}. Pozostałe oferty
            na giełdach schodzą same.{' '}
            {data?.acceptedAwaitingTms
              ? '„Sprzedane” ustawi import, gdy przewoźnik albo numer pojazdu pojawi się w TMS — wpisz go w TMS.'
              : ''}
          </Alert>
        ) : null}
        {data && data.offers.length > 0 ? (
          <OffersTable
            offers={data.offers}
            onReject={(o) => setRejecting(o)}
            onAccept={acceptanceOn ? (o) => setAccepting(o) : undefined}
          />
        ) : data ? (
          <Typography variant="body2" color="text.secondary">
            Brak zapisanych ofert.
          </Typography>
        ) : null}

        {data && !data.canAdd ? (
          <Typography variant="body2" color="text.secondary" data-testid="carrier-offers-refusal">
            {data.addRefusal}
          </Typography>
        ) : data ? (
          <Stack spacing={1} data-testid="carrier-offer-form">
            <Typography variant="subtitle2">Nowa oferta</Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Autocomplete
                size="small"
                sx={{ flex: '1 1 260px', maxWidth: 420 }}
                options={options}
                value={carrier}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(x) => x}
                onInputChange={(_, q, reason) => {
                  if (reason === 'input') void search(q);
                }}
                onChange={(_, v) => setCarrier(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Przewoźnik z kartoteki" placeholder="nazwa albo miasto" />
                )}
              />
              <TextField
                select
                size="small"
                label="Rodzaj"
                value={form.kind}
                onChange={(e) => set({ kind: e.target.value })}
                sx={{ flex: '0 1 200px' }}
              >
                {OFFER_KIND_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Kwota"
                value={form.amount}
                onChange={(e) => set({ amount: e.target.value })}
                sx={{ flex: '0 1 120px' }}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
              <TextField
                select
                size="small"
                label="Waluta"
                value={form.currency}
                onChange={(e) => set({ currency: e.target.value })}
                sx={{ flex: '0 1 100px' }}
              >
                {OFFER_CURRENCIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <TextField
                size="small"
                type="datetime-local"
                label="Ważna do (czas polski)"
                value={form.validUntil}
                onChange={(e) => set({ validUntil: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ flex: '0 1 220px' }}
              />
              <TextField
                select
                size="small"
                label="Źródło"
                value={form.source}
                onChange={(e) => set({ source: e.target.value })}
                sx={{ flex: '0 1 140px' }}
              >
                {OFFER_SOURCE_CHOICES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={form.source === 'other' ? 'Skąd oferta (wymagane)' : 'Skąd oferta (opcjonalnie)'}
                value={form.sourceNote}
                onChange={(e) => set({ sourceNote: e.target.value })}
                placeholder="np. numer oferty na giełdzie"
                sx={{ flex: '1 1 200px', maxWidth: 360 }}
                slotProps={{ htmlInput: { maxLength: 300 } }}
              />
              <TextField
                size="small"
                label="Uwagi (opcjonalnie)"
                value={form.note}
                onChange={(e) => set({ note: e.target.value })}
                sx={{ flex: '1 1 200px', maxWidth: 360 }}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
              <Button
                variant="contained"
                disabled={busy || !offerFormReady(carrier, form)}
                onClick={() => void submit()}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                // Kontrakt `data-testid` paczki (S-22.10-02): u nas kontroferta to rodzaj zapisywanej oferty.
                data-testid="trd-negocjacja-kontroferta"
              >
                Zapisz ofertę
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </Stack>

      <Dialog open={accepting !== null} onClose={() => !busy && closeAccept()} fullWidth maxWidth="sm">
        <DialogTitle>Akceptacja przewoźnika: {accepting?.carrier.name}</DialogTitle>
        <DialogContent>
          {accepting ? (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Stawka <strong>{money(accepting.amount, accepting.currency)}</strong>, marża {marginText(accepting.margin)}
              {accepting.margin.band ? ` — ${MARGIN_BAND_LABELS[accepting.margin.band] ?? accepting.margin.band}` : ''}.
              Akceptacja zapisze się w historii zlecenia, pozostałe otwarte oferty zostaną odrzucone, a oferty na
              giełdach zejdą same. Stan zlecenia się nie zmienia — „sprzedane” ustawi import z TMS.
            </Typography>
          ) : null}
          {accepting?.acceptance?.verification ? (
            // Weryfikacja przewoźnika (S-24.10-11): stan z kartoteki; niepełna
            // albo nieustalona to wyjątek z uzasadnieniem, bez blokady.
            <Typography variant="body2" sx={{ mb: 1 }} data-testid="accept-verification">
              Weryfikacja przewoźnika: <strong>{accepting.acceptance.verification.statusLabel}</strong>
              {accepting.acceptance.verification.status === 'full'
                ? '.'
                : ' — akceptacja to wyjątek z uzasadnieniem, zapisze się w historii zlecenia.'}
            </Typography>
          ) : null}
          {acceptWarnings.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 1 }} data-testid="accept-warnings">
              Ostrzeżenia: {acceptWarnings.join('; ')}. Akceptacja wymaga uzasadnienia.
            </Alert>
          ) : null}
          <TextField
            label="Warunki (opcjonalnie)"
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            placeholder="np. termin płatności, auto, godziny"
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <TextField
            label={acceptWarnings.length > 0 ? 'Uzasadnienie (wymagane)' : 'Uzasadnienie (opcjonalnie)'}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required={acceptWarnings.length > 0}
            sx={{ mt: 1 }}
            helperText={acceptWarnings.length > 0 ? `Co najmniej ${MIN_ACCEPTANCE_REASON} znaków` : undefined}
            slotProps={{ htmlInput: { maxLength: 1000, 'data-testid': 'dlg-uzasadnienie' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAccept} disabled={busy}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            disabled={busy || !acceptReady}
            onClick={() => void accept()}
            data-testid="dlg-ok"
          >
            Akceptuję przewoźnika, stawkę i warunki
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revoking !== null} onClose={() => !busy && setRevoking(null)} fullWidth maxWidth="sm">
        <DialogTitle>Cofnij akceptację: {revoking?.carrier.name}</DialogTitle>
        <DialogContent>
          <TextField
            label="Powód cofnięcia (wymagany)"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required
            sx={{ mt: 1 }}
            helperText={`Co najmniej ${MIN_ACCEPTANCE_REASON} znaków — oferta wróci do otwartych`}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoking(null)} disabled={busy}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            disabled={busy || revokeReason.trim().length < MIN_ACCEPTANCE_REASON}
            onClick={() => void revoke()}
          >
            Cofnij akceptację
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejecting !== null} onClose={() => setRejecting(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Odrzuć ofertę: {rejecting?.carrier.name} — {rejecting ? money(rejecting.amount, rejecting.currency) : ''}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Powód (opcjonalnie)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>Anuluj</Button>
          <Button variant="contained" disabled={busy} onClick={() => void reject()}>
            Odrzuć
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

const ScopedPanel = recordScoped(CarrierOffersPanel);

/**
 * Sekcja tylko dla Trade, z przełącznikiem i modułem „Przewoźnicy” spółki
 * zlecenia — inaczej API odpowie 403/404, więc sekcji nie pokazujemy wcale.
 */
export const CarrierOffers = () => {
  const record = useRecordContext<RaRecord>();
  const on = useFeature(FEATURE_CARRIER_OFFERS);
  const canTrade = useHasPermission('TRADE');
  // Centrum ról (22.10.2026): sekcja ukryta dla roli w „Co widzi”.
  const visible = useElementVisible(ELEMENT.orderCarrierOffers);
  if (!on || !canTrade || !visible || !record) return null;
  return (
    <ModuleGate module="carriers" companyId={companyIdOf(record)}>
      <ScopedPanel />
    </ModuleGate>
  );
};
